import React, { useState, useEffect, useCallback } from 'react';
import { User, Users, Bell, Palette, LogOut, Mail, Clock, CheckCircle, XCircle, Trash2, RefreshCw, Tag, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { invitationService } from '../../services/invitationService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import InvitePartnerModal from '../../components/shared/InvitePartnerModal';
import { ROUTES } from '../../utils/constants';

const Settings = () => {
  const { user, userProfile, logout, updateUserProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: userProfile?.displayName || '',
    email: user?.email || ''
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateUserProfile({
        displayName: profileData.displayName
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Load family invitations
  const loadInvitations = useCallback(async () => {
    if (!userProfile?.familyId) return;
    
    setInvitationsLoading(true);
    try {
      const result = await invitationService.getFamilyInvitations(userProfile.familyId);
      if (result.success) {
        setInvitations(result.invitations);
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  }, [userProfile?.familyId]);

  // Cancel/Delete invitation
  const handleCancelInvitation = async (invitationId) => {
    try {
      const result = await invitationService.cancelInvitation(invitationId);
      if (result.success) {
        // Refresh invitations list
        await loadInvitations();
      } else {
        console.error('Failed to cancel invitation:', result.error);
      }
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date.toDate ? date.toDate() : date).toLocaleDateString();
  };

  // Get status icon and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
      case 'accepted':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
      case 'cancelled':
        return { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
      case 'expired':
        return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
      default:
        return { icon: Clock, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  };

  // Load invitations when component mounts or familyId changes
  useEffect(() => {
    loadInvitations();
  }, [userProfile?.familyId, loadInvitations]);

  // Refresh invitations when modal closes (in case new invitation was sent)
  const handleModalClose = () => {
    setShowInviteModal(false);
    loadInvitations(); // Refresh the list
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Profile Settings</span>
          </CardTitle>
          <CardDescription>
            Update your personal information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <Input
                id="displayName"
                type="text"
                value={profileData.displayName}
                onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed from here
              </p>
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Family Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Family Settings</span>
          </CardTitle>
          <CardDescription>
            Manage your family account and invitations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Family ID</p>
                <p className="text-sm text-gray-600">{userProfile?.familyId || 'Not set'}</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Your Role</p>
                <p className="text-sm text-gray-600 capitalize">{userProfile?.role || 'Member'}</p>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowInviteModal(true)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Invite Partner
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Partner Invitations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>Partner Invitations</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadInvitations}
              disabled={invitationsLoading}
            >
              <RefreshCw className={`h-4 w-4 ${invitationsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
          <CardDescription>
            Manage invitations sent to potential family members
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitationsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading invitations...</span>
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No invitations sent yet</p>
              <p className="text-sm text-gray-400">Invite your partner to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => {
                const statusInfo = getStatusInfo(invitation.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div
                    key={invitation.id}
                    className={`p-4 rounded-lg border ${statusInfo.bg} ${statusInfo.border}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                          <div>
                            <p className="font-medium text-gray-900">{invitation.invitedEmail}</p>
                            <p className="text-sm text-gray-600 capitalize">
                              Status: {invitation.status}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Sent:</span> {formatDate(invitation.createdAt)}
                          </div>
                          <div>
                            <span className="font-medium">Expires:</span> {formatDate(invitation.expiresAt)}
                          </div>
                        </div>
                        
                        {invitation.acceptedAt && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Accepted:</span> {formatDate(invitation.acceptedAt)}
                          </div>
                        )}
                      </div>
                      
                      {invitation.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelInvitation(invitation.id)}
                          className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="h-5 w-5" />
            <span>Preferences</span>
          </CardTitle>
          <CardDescription>
            Customize your app experience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Link to={ROUTES.SETTINGS_TAGS}>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <div className="flex items-center space-x-3">
                  <Tag className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Expense Tags</p>
                    <p className="text-sm text-gray-600">Manage tags for expense organization</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Currency</p>
                <p className="text-sm text-gray-600">Bangladeshi Taka (৳)</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Date Format</p>
                <p className="text-sm text-gray-600">DD/MM/YYYY</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Theme</p>
                <p className="text-sm text-gray-600">Light</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bell className="h-5 w-5" />
            <span>Notifications</span>
          </CardTitle>
          <CardDescription>
            Control your notification preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Budget Alerts</p>
                <p className="text-sm text-gray-600">Get notified when approaching budget limits</p>
              </div>
              <div className="text-green-600 font-medium">Enabled</div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Partner Activity</p>
                <p className="text-sm text-gray-600">Get notified of partner's expense activities</p>
              </div>
              <div className="text-green-600 font-medium">Enabled</div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Weekly Reports</p>
                <p className="text-sm text-gray-600">Receive weekly spending summaries</p>
              </div>
              <div className="text-green-600 font-medium">Enabled</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Account Actions</CardTitle>
          <CardDescription>
            Manage your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              className="w-full flex items-center space-x-2"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invite Partner Modal */}
      <InvitePartnerModal 
        isOpen={showInviteModal}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default Settings;
